/**
 * Sales List Page
 * 영업 관리 목록 페이지
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSales } from '../../use-cases/useSales';
import { useAdminAuth } from '../../use-cases';
import {
  SalesLead,
  getSalesStatusText,
  getSalesStatusColor,
  formatDate,
  formatCurrency,
} from '../../models/SalesLead';
import { SalesDetailModal } from './SalesDetailModal';
import { SalesCreateModal } from './SalesCreateModal';

type ViewMode = 'new_inquiry' | 'consulting' | 'contracted' | 'all';

export const SalesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewMode = (searchParams.get('view') as ViewMode) || 'new_inquiry';

  const { currentUser } = useAdminAuth();
  const {
    leads,
    loading,
    error,
    statistics,
    fetchAll,
    fetchNewInquiry,
    fetchConsulting,
    fetchContracted,
    fetchStatistics,
  } = useSales();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState<SalesLead | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    switch (viewMode) {
      case 'new_inquiry':
        fetchNewInquiry();
        break;
      case 'consulting':
        fetchConsulting();
        break;
      case 'contracted':
        fetchContracted();
        break;
      case 'all':
        fetchAll();
        break;
    }
    fetchStatistics();
  }, [viewMode, fetchNewInquiry, fetchConsulting, fetchContracted, fetchAll, fetchStatistics]);

  const filteredLeads = leads.filter((lead) => {
    const query = searchQuery.toLowerCase();
    return (
      lead.company_name.toLowerCase().includes(query) ||
      lead.contact_name.toLowerCase().includes(query) ||
      lead.contact_phone?.includes(query) ||
      lead.contact_email?.toLowerCase().includes(query)
    );
  });

  const handleLeadClick = (lead: SalesLead) => {
    setSelectedLead(lead);
  };

  const handleModalClose = () => {
    setSelectedLead(null);
    // 목록 새로고침
    switch (viewMode) {
      case 'new_inquiry':
        fetchNewInquiry();
        break;
      case 'consulting':
        fetchConsulting();
        break;
      case 'contracted':
        fetchContracted();
        break;
      case 'all':
        fetchAll();
        break;
    }
    fetchStatistics();
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchConsulting();
    fetchStatistics();
  };

  const getStatusBadgeColor = (status: string): string => {
    const colors: Record<string, string> = {
      new_inquiry: 'bg-purple-100 text-purple-800',
      consulting: 'bg-blue-100 text-blue-800',
      quote_sent: 'bg-yellow-100 text-yellow-800',
      negotiating: 'bg-orange-100 text-orange-800',
      contracted: 'bg-green-100 text-green-800',
      lost: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">💼 영업 관리</h1>
          <p className="text-gray-600 mt-1">잠재 고객 상담부터 계약까지 관리</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>새 영업 등록</span>
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-blue-600">{statistics.active}</div>
            <div className="text-sm text-gray-600">상담 진행중</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-green-600">{statistics.contracted}</div>
            <div className="text-sm text-gray-600">계약 완료</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-gray-600">{statistics.lost}</div>
            <div className="text-sm text-gray-600">영업 실패</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-indigo-600">
              {statistics.contracted > 0
                ? Math.round((statistics.contracted / (statistics.contracted + statistics.lost)) * 100)
                : 0}%
            </div>
            <div className="text-sm text-gray-600">계약 성공률</div>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { id: 'new_inquiry', label: '신규문의', icon: '📬', count: statistics?.new_inquiry },
              { id: 'consulting', label: '상담중', icon: '💬', count: statistics?.active },
              { id: 'contracted', label: '계약완료', icon: '✅', count: statistics?.contracted },
              { id: 'all', label: '전체', icon: '📋', count: statistics?.total },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigate(`/admin/sales?view=${tab.id}`)}
                className={`px-6 py-3 border-b-2 font-medium text-sm ${
                  viewMode === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon} {tab.label} {tab.count !== undefined && `(${tab.count})`}
              </button>
            ))}
          </nav>
        </div>

        {/* Search */}
        <div className="p-4">
          <input
            type="text"
            placeholder="업체명, 담당자명, 연락처로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Loading */}
        {loading && (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">로딩 중...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="m-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Lead List */}
        {!loading && !error && (
          <div className="divide-y divide-gray-200">
            {filteredLeads.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                {searchQuery ? '검색 결과가 없습니다.' : '영업 건이 없습니다.'}
              </div>
            ) : (
              filteredLeads.map((lead) => (
                <div
                  key={lead.id}
                  onClick={() => handleLeadClick(lead)}
                  className="p-6 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    {/* Lead Info */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {lead.company_name}
                        </h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(lead.status)}`}>
                          {getSalesStatusText(lead.status)}
                        </span>
                      </div>

                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
                        <div>
                          <span className="text-gray-400">담당자:</span> {lead.contact_name}
                        </div>
                        <div>
                          <span className="text-gray-400">연락처:</span> {lead.contact_phone}
                        </div>
                        <div>
                          <span className="text-gray-400">예상금액:</span> {formatCurrency(lead.contract_amount)}
                        </div>
                        <div>
                          <span className="text-gray-400">예상계약일:</span> {formatDate(lead.expected_contract_date)}
                        </div>
                      </div>

                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        {lead.assigned_to_name && (
                          <span>👤 {lead.assigned_to_name}</span>
                        )}
                        <span>🗓️ {formatDate(lead.created_at)}</span>
                      </div>
                    </div>

                    {/* Arrow Icon */}
                    <div className="ml-4 text-gray-400">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLead && (
        <SalesDetailModal
          lead={selectedLead}
          currentUser={currentUser}
          onClose={handleModalClose}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <SalesCreateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};
